import React, { useState, useEffect } from 'react';
import { X, Search, FileText, Mail, MessageSquare, Send, User, Loader2, CheckCircle, AlertCircle, ChevronLeft, ArrowRight } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabaseClient';
import { sendEmailWithFallback } from '../lib/email';
import { sendSMS } from '../lib/messaging';
import { fetchAccountsReceivable, ArOpenInvoice } from '../lib/invoices/accountsReceivable';

interface SendStatementModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  /** Legacy completed bookings (no invoice row) merged in so A/R is complete. */
  bookings?: any[];
}

interface OverdueCustomer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  invoiceCount: number;
  totalOutstanding: number;
  /** Open A/R rows for this customer — same data the Receivables tab shows. */
  openInvoices: ArOpenInvoice[];
}

interface UnpaidInvoice {
  id: string;
  kind: 'invoice' | 'legacy_booking';
  bookingId: string | null;
  invoiceNumber: string;
  date: string;
  description: string;
  total: number;
  paid: number;
  balance: number;
}

// No `bookings` default: undefined tells the A/R engine to fetch legacy
// completed bookings itself, so statements include pre-invoicing balances.
export default function SendStatementModal({ isOpen, onClose, businessId, businessName, bookings }: SendStatementModalProps) {
  const [overdueCustomers, setOverdueCustomers] = useState<OverdueCustomer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<OverdueCustomer[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');

  const [selectedCustomer, setSelectedCustomer] = useState<OverdueCustomer | null>(null);
  const [unpaidInvoices, setUnpaidInvoices] = useState<UnpaidInvoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  const [sendEmail, setSendEmail] = useState(true);
  const [sendSmsEnabled, setSendSmsEnabled] = useState(true);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [smsMessage, setSmsMessage] = useState('');

  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true));
      fetchOverdueCustomers();
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isVisible && !isOpen) {
      setOverdueCustomers([]);
      setFilteredCustomers([]);
      setCustomerSearch('');
      setSelectedCustomer(null);
      setUnpaidInvoices([]);
      setSendEmail(true);
      setSendSmsEnabled(true);
      setEmailSubject('');
      setEmailMessage('');
      setSmsMessage('');
      setIsSending(false);
      setSendResult(null);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const fetchOverdueCustomers = async () => {
    if (!businessId) return;
    setIsLoadingCustomers(true);
    try {
      // SINGLE SOURCE OF TRUTH: the same A/R builder the Receivables tab uses
      // (invoices_ledger + legacy bookings fallback). Statements must always
      // match the Receivables numbers exactly.
      const ar = await fetchAccountsReceivable(businessId, bookings);
      const groups = ar.customers.filter((g) => g.customerId);

      // A/R groups carry no contact info — resolve email/phone in one query.
      const ids = groups.map((g) => g.customerId!) ;
      const contactById = new Map<string, any>();
      if (ids.length > 0) {
        const { data: contacts } = await supabase
          .from('customers')
          .select('id, first_name, last_name, email, phone')
          .in('id', ids);
        for (const c of contacts || []) contactById.set(c.id, c);
      }

      const sorted: OverdueCustomer[] = groups.map((g) => {
        const c = contactById.get(g.customerId!) || {};
        return {
          id: g.customerId!,
          first_name: c.first_name || g.customerName.split(' ')[0] || '',
          last_name: c.last_name || g.customerName.split(' ').slice(1).join(' ') || '',
          email: c.email || '',
          phone: c.phone || '',
          invoiceCount: g.invoiceCount,
          totalOutstanding: g.outstanding,
          openInvoices: g.invoices,
        };
      });
      setOverdueCustomers(sorted);
      setFilteredCustomers(sorted);
    } catch (err) {
      console.error('Error fetching overdue customers:', err);
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  useEffect(() => {
    if (!customerSearch.trim()) {
      setFilteredCustomers(overdueCustomers);
      return;
    }
    const q = customerSearch.toLowerCase();
    setFilteredCustomers(
      overdueCustomers.filter(c =>
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phone && c.phone.includes(q))
      )
    );
  }, [customerSearch, overdueCustomers]);

  const selectCustomer = async (customer: OverdueCustomer) => {
    setSelectedCustomer(customer);
    setSendResult(null);
    await fetchUnpaidInvoices(customer);
  };

  const fetchUnpaidInvoices = async (customer: OverdueCustomer) => {
    setIsLoadingInvoices(true);
    try {
      // The open invoices came with the A/R fetch — identical to the
      // Receivables tab. Only descriptions need resolving here.
      const invoiceIds = customer.openInvoices.filter((i) => i.kind === 'invoice').map((i) => i.id);
      const descByInvoice = new Map<string, string>();
      if (invoiceIds.length > 0) {
        const { data: items } = await supabase
          .from('invoice_line_items')
          .select('invoice_id, description')
          .in('invoice_id', invoiceIds);
        for (const it of items || []) {
          if (!descByInvoice.has(it.invoice_id)) descByInvoice.set(it.invoice_id, it.description);
        }
      }

      const invoices: UnpaidInvoice[] = customer.openInvoices.map((inv) => ({
        id: inv.id,
        kind: inv.kind === 'legacy_booking' ? 'legacy_booking' : 'invoice',
        bookingId: inv.bookingId,
        invoiceNumber: inv.number,
        date: inv.invoiceDate || inv.dueDate || new Date().toISOString(),
        description: descByInvoice.get(inv.id) || (inv.bookingId ? 'Booking' : 'Invoice'),
        total: inv.total,
        paid: Math.max(0, Math.round((inv.total - inv.balance) * 100) / 100),
        balance: inv.balance,
      }));

      invoices.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      setUnpaidInvoices(invoices);

      const totalOutstanding = invoices.reduce((s, i) => s + i.balance, 0);
      const name = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Customer';

      setEmailSubject(`Statement of Account from ${businessName}`);
      setEmailMessage(
        `Hi ${name},\n\nPlease find attached your statement of account showing ${invoices.length} outstanding invoice${invoices.length !== 1 ? 's' : ''} totaling $${totalOutstanding.toFixed(2)}.\n\nIf you have any questions, please don't hesitate to get in touch.\n\nThank you,\n${businessName}`
      );
      setSmsMessage(
        `Hi ${name}, you have ${invoices.length} outstanding invoice${invoices.length !== 1 ? 's' : ''} totaling $${totalOutstanding.toFixed(2)} with ${businessName}. Please contact us for payment details.`
      );
    } catch (err) {
      console.error('Error fetching unpaid invoices:', err);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const totalOutstanding = unpaidInvoices.reduce((s, i) => s + i.balance, 0);

  const handleSend = async () => {
    if (!selectedCustomer || unpaidInvoices.length === 0) return;
    if (!sendEmail && !sendSmsEnabled) return;

    setIsSending(true);
    setSendResult(null);

    try {
      const invoiceIds = unpaidInvoices.filter(i => i.kind === 'invoice').map(i => i.id);
      const bookingIds = unpaidInvoices.filter(i => i.kind === 'legacy_booking').map(i => i.id);

      const { data: pdfData, error: pdfError } = await supabase.functions.invoke('generate-statement-pdf', {
        body: { businessId, customerId: selectedCustomer.id, invoiceIds, bookingIds }
      });

      if (pdfError || !pdfData) {
        throw new Error(pdfData?.error || pdfError?.message || 'Failed to generate statement PDF');
      }

      const errors: string[] = [];
      let emailSent = false;
      let smsSent = false;

      if (sendEmail && selectedCustomer.email) {
        const invoiceTableRows = unpaidInvoices.map(inv => {
          const dateStr = new Date(inv.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          return `
            <tr>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #374151;">${inv.invoiceNumber}</td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #6b7280;">${dateStr}</td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #374151; text-align: right;">$${inv.total.toFixed(2)}</td>
              <td style="padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 14px; color: #dc2626; font-weight: 600; text-align: right;">$${inv.balance.toFixed(2)}</td>
            </tr>`;
        }).join('\n');

        const messageHtml = (emailMessage || '').split('\n').map(line =>
          `<p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 8px 0;">${line || '&nbsp;'}</p>`
        ).join('\n');

        const emailBody = `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
            <div style="padding: 40px 32px 32px 32px;">
              <div style="margin-bottom: 32px;">
                <h1 style="color: #000000; margin: 0 0 8px 0; font-size: 28px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.2;">Statement of Account</h1>
                <p style="color: #6b7280; margin: 0; font-size: 15px; line-height: 1.5;">${businessName}</p>
              </div>
              ${messageHtml}
              <div style="margin-top: 24px; border-radius: 8px; overflow: hidden; border: 1px solid #e5e7eb;">
                <table style="width: 100%; border-collapse: collapse;">
                  <thead>
                    <tr style="background: #f9fafb;">
                      <th style="padding: 10px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em;">Invoice #</th>
                      <th style="padding: 10px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em;">Date</th>
                      <th style="padding: 10px 12px; text-align: right; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em;">Amount</th>
                      <th style="padding: 10px 12px; text-align: right; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em;">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${invoiceTableRows}
                  </tbody>
                  <tfoot>
                    <tr style="background: #f9fafb;">
                      <td colspan="3" style="padding: 10px 12px; font-size: 14px; font-weight: 700; color: #111827;">Total Outstanding</td>
                      <td style="padding: 10px 12px; font-size: 14px; font-weight: 700; color: #dc2626; text-align: right;">$${totalOutstanding.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            <div style="padding: 24px 32px; background: #f9fafb; border-top: 1px solid #e5e7eb;">
              <p style="color: #9ca3af; font-size: 13px; margin: 0; text-align: center; line-height: 1.5;">This is an automated statement from ${businessName}</p>
            </div>
          </div>
        `;

        const result = await sendEmailWithFallback({
          to: selectedCustomer.email,
          subject: emailSubject,
          body: emailBody,
          businessId,
          attachments: [{
            filename: pdfData.filename,
            content: pdfData.base64,
            type: 'application/pdf'
          }]
        });

        if (result.success) {
          emailSent = true;
        } else {
          errors.push(`Email: ${result.error || 'Failed to send'}`);
        }
      }

      if (sendSmsEnabled && selectedCustomer.phone) {
        const result = await sendSMS({
          businessId,
          to: selectedCustomer.phone,
          message: smsMessage,
          customerId: selectedCustomer.id,
        });

        if (result.success) {
          smsSent = true;
        } else {
          errors.push(`SMS: ${result.error || 'Failed to send'}`);
        }
      }

      const custFullName = `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim();
      for (const inv of unpaidInvoices.filter(i => i.bookingId)) {
        if (emailSent) {
          await supabase.from('invoice_history').insert({
            business_id: businessId,
            booking_id: inv.bookingId,
            booking_number: inv.invoiceNumber,
            sent_via: 'statement_email',
            has_payment_link: false,
            payment_link_url: null,
            customer_email: selectedCustomer.email || null,
            customer_name: custFullName || null,
          });
        }
        if (smsSent) {
          await supabase.from('invoice_history').insert({
            business_id: businessId,
            booking_id: inv.bookingId,
            booking_number: inv.invoiceNumber,
            sent_via: 'statement_sms',
            has_payment_link: false,
            payment_link_url: null,
            customer_email: null,
            customer_name: custFullName || null,
          });
        }
      }

      if (errors.length > 0 && !emailSent && !smsSent) {
        setSendResult({ type: 'error', message: errors.join('. ') });
      } else if (errors.length > 0) {
        const sent = [emailSent && 'Email', smsSent && 'SMS'].filter(Boolean).join(' & ');
        setSendResult({ type: 'success', message: `Statement sent via ${sent}. Some channels failed: ${errors.join('. ')}` });
      } else {
        const sent = [emailSent && 'Email', smsSent && 'SMS'].filter(Boolean).join(' & ');
        setSendResult({ type: 'success', message: `Statement sent successfully via ${sent}!` });
      }
    } catch (err: any) {
      console.error('Send statement error:', err);
      setSendResult({ type: 'error', message: err.message || 'Failed to send statement' });
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen && !isVisible) return null;

  const canSend = selectedCustomer && unpaidInvoices.length > 0 && (sendEmail || sendSmsEnabled) && !isSending &&
    (sendEmail ? !!selectedCustomer.email : true) && (sendSmsEnabled ? !!selectedCustomer.phone : true);

  const grandTotalOutstanding = overdueCustomers.reduce((s, c) => s + c.totalOutstanding, 0);

  return createPortal(
    <div className="fixed inset-0 z-[99999]">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
        onClick={handleClose}
      />

      {/* Right-side panel */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[480px] bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        onClick={(e) => e.stopPropagation()}
        onTransitionEnd={handleTransitionEnd}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/60 bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            {selectedCustomer && (
              <button
                onClick={() => {
                  setSelectedCustomer(null);
                  setUnpaidInvoices([]);
                  setSendResult(null);
                  setSendEmail(true);
                  setSendSmsEnabled(true);
                }}
                className="p-1.5 -ml-1 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-500" />
              </button>
            )}
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-[17px] font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.02em' }}>
                {selectedCustomer ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}` : 'Send Statement'}
              </h2>
              <p className="text-[12px] text-gray-500 truncate">
                {selectedCustomer
                  ? `${unpaidInvoices.length} unpaid invoice${unpaidInvoices.length !== 1 ? 's' : ''} · $${totalOutstanding.toFixed(2)} owing`
                  : `${overdueCustomers.length} customer${overdueCustomers.length !== 1 ? 's' : ''} with unpaid invoices`
                }
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 rounded-xl hover:bg-gray-100 transition-colors flex-shrink-0">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        {!selectedCustomer ? (
          /* ─── Customer List View ─── */
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search */}
            <div className="px-4 pt-3 pb-2 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Search customers..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[14px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all"
                  autoFocus
                />
              </div>
            </div>

            {/* Summary bar */}
            {!isLoadingCustomers && overdueCustomers.length > 0 && (
              <div className="px-4 pb-2 flex-shrink-0">
                <div className="flex items-center justify-between bg-red-50 rounded-xl px-3.5 py-2.5 border border-red-100">
                  <span className="text-[12px] font-medium text-red-700">
                    {overdueCustomers.length} customer{overdueCustomers.length !== 1 ? 's' : ''} · {overdueCustomers.reduce((s, c) => s + c.invoiceCount, 0)} invoices
                  </span>
                  <span className="text-[13px] font-bold text-red-700">${grandTotalOutstanding.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Customer list */}
            <div className="flex-1 overflow-y-auto">
              {isLoadingCustomers ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin mb-3" />
                  <p className="text-[13px] text-gray-500">Loading customers with unpaid invoices...</p>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mb-4">
                    <CheckCircle className="w-7 h-7 text-green-500" />
                  </div>
                  <p className="text-[15px] font-semibold text-gray-700 mb-1">
                    {customerSearch ? 'No matches found' : 'All caught up!'}
                  </p>
                  <p className="text-[13px] text-gray-500 text-center">
                    {customerSearch
                      ? 'Try a different search term.'
                      : 'No customers have unpaid invoices right now.'
                    }
                  </p>
                </div>
              ) : (
                <div className="px-3 pb-3">
                  {filteredCustomers.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => selectCustomer(c)}
                      className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all text-left group"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-[14px] font-bold text-gray-500">
                          {(c.first_name?.[0] || '').toUpperCase()}{(c.last_name?.[0] || '').toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-semibold text-gray-900 truncate">{c.first_name} {c.last_name}</p>
                        <p className="text-[12px] text-gray-500 truncate">
                          {c.invoiceCount} invoice{c.invoiceCount !== 1 ? 's' : ''}
                          {c.email ? ` · ${c.email}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[14px] font-bold text-red-600">${c.totalOutstanding.toFixed(2)}</span>
                        <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ─── Selected Customer Detail View ─── */
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {/* Unpaid Invoices */}
              <div>
                <label className="block text-[13px] font-semibold text-gray-700 mb-1.5" style={{ letterSpacing: '-0.01em' }}>
                  Unpaid Invoices
                </label>
                {isLoadingInvoices ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    <span className="ml-2 text-[13px] text-gray-500">Loading invoices...</span>
                  </div>
                ) : unpaidInvoices.length === 0 ? (
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-[14px] font-medium text-gray-700">All caught up!</p>
                    <p className="text-[12px] text-gray-500 mt-0.5">This customer has no unpaid invoices.</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="max-h-52 overflow-y-auto">
                      {unpaidInvoices.map((inv) => (
                        <div key={inv.id} className="flex items-center justify-between px-3.5 py-2.5 border-b border-gray-100 last:border-0">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[13px] font-semibold text-gray-900">#{inv.invoiceNumber}</span>
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                                inv.bookingId ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'
                              }`}>
                                {inv.bookingId ? 'Booking' : 'Invoice'}
                              </span>
                            </div>
                            <p className="text-[12px] text-gray-500 truncate mt-0.5">
                              {inv.description} · {new Date(inv.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <p className="text-[13px] font-bold text-red-600">${inv.balance.toFixed(2)}</p>
                            {inv.paid > 0 && (
                              <p className="text-[11px] text-gray-400">of ${inv.total.toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gray-50 px-3.5 py-2.5 flex items-center justify-between border-t border-gray-200">
                      <span className="text-[13px] font-semibold text-gray-700">{unpaidInvoices.length} invoice{unpaidInvoices.length !== 1 ? 's' : ''}</span>
                      <span className="text-[14px] font-bold text-red-600">Total: ${totalOutstanding.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Email & SMS Composition */}
              {unpaidInvoices.length > 0 && (
                <>
                  {/* Email */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-500" />
                        <span className="text-[13px] font-semibold text-gray-700">Email</span>
                        {!selectedCustomer.email && (
                          <span className="text-[11px] text-orange-500 font-medium">No email on file</span>
                        )}
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendEmail}
                          onChange={(e) => setSendEmail(e.target.checked)}
                          disabled={!selectedCustomer.email}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50" />
                      </label>
                    </div>
                    {sendEmail && selectedCustomer.email && (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          placeholder="Subject"
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[13px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                        />
                        <textarea
                          value={emailMessage}
                          onChange={(e) => setEmailMessage(e.target.value)}
                          rows={4}
                          placeholder="Message..."
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[13px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 resize-none"
                        />
                        <p className="text-[11px] text-gray-400">Statement PDF will be attached. Inline invoice table included in email body.</p>
                      </div>
                    )}
                  </div>

                  {/* SMS */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-green-500" />
                        <span className="text-[13px] font-semibold text-gray-700">SMS</span>
                        {!selectedCustomer.phone && (
                          <span className="text-[11px] text-orange-500 font-medium">No phone on file</span>
                        )}
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={sendSmsEnabled}
                          onChange={(e) => setSendSmsEnabled(e.target.checked)}
                          disabled={!selectedCustomer.phone}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600 peer-disabled:opacity-50" />
                      </label>
                    </div>
                    {sendSmsEnabled && selectedCustomer.phone && (
                      <div className="space-y-2">
                        <textarea
                          value={smsMessage}
                          onChange={(e) => setSmsMessage(e.target.value)}
                          rows={3}
                          placeholder="SMS message..."
                          className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[13px] text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-300 resize-none"
                        />
                        <p className="text-[11px] text-gray-400">{smsMessage.length} characters</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Result */}
              {sendResult && (
                <div className={`flex items-start gap-2.5 p-3.5 rounded-xl ${
                  sendResult.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  {sendResult.type === 'success' ? (
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  )}
                  <p className={`text-[13px] font-medium ${sendResult.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                    {sendResult.message}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            {unpaidInvoices.length > 0 && (
              <div className="px-5 py-4 border-t border-gray-200/60 bg-gray-50/50 flex-shrink-0">
                {(!sendResult || sendResult.type === 'error') ? (
                  <button
                    onClick={handleSend}
                    disabled={!canSend}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-[14px] font-semibold rounded-xl transition-all ${
                      canSend
                        ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 shadow-sm'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending Statement...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Statement
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setSelectedCustomer(null);
                      setUnpaidInvoices([]);
                      setSendResult(null);
                      fetchOverdueCustomers();
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-[14px] font-semibold rounded-xl bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back to Customers
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
